import { Link, useLocation } from 'react-router-dom'
import {
  MessageSquare,
  LayoutDashboard,
  Cpu,
  Compass,
  ScrollText,
  Settings,
  Zap,
  Menu,
  X,
  Brain,
  Users,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useWebSocketStore } from '@/stores/websocket'
import { useSystemStore } from '@/stores/system'

interface LayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Chat', href: '/chat', icon: MessageSquare },
  { name: 'Models', href: '/models', icon: Cpu },
  { name: 'Memory', href: '/memory', icon: Brain },
  { name: 'Agents', href: '/agents', icon: Users },
  { name: 'Discovery', href: '/tasks', icon: Compass },
  { name: 'Logs', href: '/logs', icon: ScrollText },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { isConnected } = useWebSocketStore()
  const { stats, health, fetchHealth } = useSystemStore()

  useEffect(() => {
    fetchHealth()
  }, [fetchHealth])

  const gpuSummary = stats?.gpus.length
    ? `${stats.gpus.length}x ${stats.gpus[0]?.name || 'GPU'}`
    : 'No GPUs'
  const version = health?.version || 'v1.0'

  return (
    <div className="h-screen flex overflow-hidden bg-background">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border shadow-xl shadow-black/40">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="text-xl font-bold tracking-tight text-foreground">
                OMTAE
              </span>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors',
                    location.pathname === item.href
                      ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-64 flex-col border-r border-border bg-card/80 backdrop-blur-md">
        <div className="flex items-center space-x-2 p-4 border-b border-border">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-primary/25">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <span className="text-xl font-bold tracking-tight">OMTAE</span>
          <div
            className={cn(
              'ml-auto h-2 w-2 rounded-full ring-2 ring-background',
              isConnected ? 'bg-emerald-500 shadow-[0_0_8px_hsl(142_71%_45%/0.6)]' : 'bg-red-500'
            )}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors',
                location.pathname === item.href
                  ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-border text-xs text-muted-foreground">
          <p className="font-medium text-foreground/80">OMTAE-Solutions {version}</p>
          <p className="mt-1">{gpuSummary}</p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-border bg-card/90 backdrop-blur-md">
          <div className="flex items-center space-x-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-primary/25">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xl font-bold tracking-tight">OMTAE</span>
          </div>
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8 bg-gradient-to-b from-background to-muted/20">
          {children}
        </main>
      </div>
    </div>
  )
}
